import { Loadable } from '@/components/loaders';
import { BaseModal } from '@/lib/Modals';
import { CustomConnectorDto } from '@/models/connectors';
import { useDeleteConnectorInstanceMutation, useGetConnectorInstancesQuery } from '@/services/endpoints/workspace-connectors';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface CustomConnectorDeauthModalProps {
  workspaceId: string;
  connector: CustomConnectorDto;
  onSuccess: () => void;
  onClose: () => void;
}

export const CustomConnectorDeauthModal: FC<CustomConnectorDeauthModalProps> = ({ workspaceId, connector, onSuccess, onClose }) => {
  const { t } = useTranslation();
  const [deleteInstance, { isLoading: isDeleting }] = useDeleteConnectorInstanceMutation();
  const { data: instances } = useGetConnectorInstancesQuery({ workspaceId });
  const [forceDelete, setForceDelete] = useState(false);

  const handleDeauthenticate = async () => {
    try {
      const instance = instances?.find((inst) => inst.connectorId === connector.id);

      if (!instance) {
        console.error('No connector instance found for this connector');
        return;
      }

      await deleteInstance({
        workspaceId,
        id: instance.id,
        force: forceDelete,
      }).unwrap();

      onSuccess();
    } catch (error) {
      console.error('Failed to deauthenticate connector:', error);
    }
  };

  return (
    <BaseModal
      onClose={onClose}
      classNames={{
        dialog: 'max-w-md w-full',
        content: 'min-h-0 p-0',
      }}
    >
      <div className='p-6'>
        <h2 className='mb-4 text-xl font-semibold'>{t('destinations:deauth_modal.title', { connectorName: connector.name })}</h2>

        <p className='mb-4 text-gray-600'>{t('destinations:deauth_modal.description')}</p>

        <div className='mb-4'>
          <label className='flex items-center'>
            <input type='checkbox' checked={forceDelete} onChange={(e) => setForceDelete(e.target.checked)} className='mr-2' />
            <span className='text-sm text-gray-700'>{t('destinations:deauth_modal.force_disconnect_label')}</span>
          </label>
        </div>

        <div className='flex justify-end gap-3'>
          <button onClick={onClose} className='rounded border border-gray-300 px-4 py-2 hover:bg-gray-50'>
            {t('destinations:deauth_modal.cancel_button')}
          </button>
          <Loadable isLoading={isDeleting}>
            <button onClick={handleDeauthenticate} className='rounded bg-red-600 px-4 py-2 text-white-100 hover:bg-red-700'>
              {t('destinations:deauth_modal.disconnect_button')}
            </button>
          </Loadable>
        </div>
      </div>
    </BaseModal>
  );
};
